import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import CompanyLogo from "@/components/CompanyLogo";
import { TagCategoryDisplay } from "@/components/TagCategorySelector";
import { MapPin, Globe, Linkedin, Twitter, Facebook, Instagram, Youtube, Users, Wine, ArrowLeft, Calendar, Package, Star, Play, FileText, ExternalLink, Mail, CalendarClock, Crown, Phone, MessageCircle, Trophy } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/")) ? `${BACKEND}${u}` : u;

export default function MemberProfile() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [news, setNews] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get(`/companies/${slug}`, { params: { lang } }).then((r) => {
      setC(r.data);
      api.get("/news", { params: { lang } }).then((rn) => setNews(rn.data.filter((n) => n.company_id === r.data.id).slice(0, 4)));
      api.get(`/companies/${r.data.id}/products`).then((rp) => setProducts(rp.data));
    }).catch(() => {});
  }, [slug, lang]);

  if (!c) return <PublicLayout><div className="py-20 text-center text-neutral-500">{t("loading")}</div></PublicLayout>;

  const social = [
    { k: "linkedin", I: Linkedin, label: "LinkedIn" },
    { k: "twitter", I: Twitter, label: "X" },
    { k: "facebook", I: Facebook, label: "Facebook" },
    { k: "instagram", I: Instagram, label: "Instagram" },
    { k: "youtube", I: Youtube, label: "YouTube" },
  ].filter((s) => c[s.k]);

  return (
    <PublicLayout>
      <Seo
        title={c.name}
        description={c.tagline || c.description?.slice(0, 160)}
        path={`/members/${c.slug}`}
        image={c.banner ? resolveUrl(c.banner) : undefined}
        type="profile"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: c.name,
          description: c.description,
          url: c.website,
          logo: resolveUrl(c.logo),
          image: c.banner ? resolveUrl(c.banner) : undefined,
          address: { "@type": "PostalAddress", addressLocality: c.city, addressCountry: c.country },
          foundingDate: c.founded_year ? `${c.founded_year}` : undefined,
          sameAs: [c.linkedin, c.twitter, c.facebook, c.instagram, c.youtube].filter(Boolean),
        }}
      />
      {user && (
        <div className="border-b border-wine-100 bg-wine-50/40">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-6 py-2.5">
            <span className="text-xs font-medium text-neutral-600">{t("comp_memberprofile_90")} <span className="font-bold text-wine-700">{user.name}</span></span>
            <Link to={user.role === "admin" ? "/admin" : user.role === "journalist" ? "/press" : "/portal"} className="inline-flex items-center gap-1 rounded-full bg-wine-500 px-4 py-1.5 text-xs font-semibold text-white hover:bg-wine-600" data-testid="member-back-to-portal">{t("comp_memberprofile_91")}</Link>
          </div>
        </div>
      )}
      <Link to="/members" className="mx-auto block max-w-6xl px-6 pt-6 text-sm font-semibold text-wine-600 hover:text-wine-700" data-testid="back-to-members">
        <ArrowLeft className="inline h-4 w-4" /> {t("back_to_members")}
      </Link>

      {/* Hero — logo embedded in cover image with overlay gradient */}
      <div className="mx-auto mt-4 max-w-6xl px-6">
        <div className="relative overflow-hidden rounded-3xl border border-neutral-100 bg-neutral-900 shadow-md" data-testid="member-profile-hero">
          {/* Cover image as background (decorative) */}
          <div className="relative h-[420px] md:h-[480px]">
            {c.banner ? (
              <img
                src={resolveUrl(c.banner)}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full object-cover"
                data-testid="member-profile-cover"
              />
            ) : (
              <img
                src="/defaults/member-bg.png"
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full object-cover"
                data-testid="member-profile-cover-default"
              />
            )}
            {/* Dark gradient overlay for readability — kept light so the pink background remains visible */}
            <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/75 via-neutral-900/25 to-transparent" aria-hidden="true" />
            {/* Subtle radial glow behind logo */}
            <div className="absolute left-8 bottom-32 h-48 w-48 rounded-full bg-wine-500/25 blur-3xl md:left-12 md:bottom-36" aria-hidden="true" />

            {/* Content overlay */}
            <div className="relative flex h-full flex-col justify-end p-7 md:p-12">
              <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                {/* Left: logo + identity */}
                <div className="flex items-end gap-5">
                  <div className="rounded-2xl bg-white/95 p-3 shadow-2xl ring-1 ring-white/40 backdrop-blur-sm">
                    <CompanyLogo
                      src={resolveUrl(c.logo)}
                      name={c.name}
                      size={96}
                      className="rounded-xl"
                    />
                  </div>
                  <div className="pb-1 min-w-0">
                    {c.main_pole && (
                      <span className="inline-block rounded-full bg-wine-500/95 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm backdrop-blur-sm">
                        {c.main_pole}
                      </span>
                    )}
                    <h1
                      className="mt-2 font-serif text-4xl font-medium leading-tight text-white drop-shadow-lg md:text-5xl"
                      data-testid="member-profile-name"
                    >
                      {c.name}
                    </h1>
                    {c.tagline && (
                      <p className="mt-1.5 max-w-xl text-base text-white/85 drop-shadow md:text-lg">{c.tagline}</p>
                    )}
                  </div>
                </div>

                {/* Right: website + social */}
                <div className="flex flex-col items-stretch gap-3 md:items-end" data-testid="member-profile-actions">
                  {c.website && (
                    <a
                      href={c.website}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-wine-700 shadow-md transition-all hover:bg-wine-50 hover:shadow-lg"
                      data-testid="member-profile-website"
                    >
                      <Globe className="h-4 w-4" /> {t("cta_visit_website")}
                    </a>
                  )}
                  {social.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {social.map((s) => (
                        <a
                          key={s.k}
                          href={c[s.k]}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={s.label}
                          title={s.label}
                          className="grid h-9 w-9 place-items-center rounded-full bg-white/15 text-white ring-1 ring-white/30 backdrop-blur-md transition-all hover:bg-white/25 hover:ring-white/60"
                          data-testid={`social-${s.k}`}
                        >
                          <s.I className="h-4 w-4" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Metadata strip below hero */}
          <div className="border-t border-neutral-100 bg-white px-7 py-4 md:px-12">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-neutral-700">
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-wine-600" /> {c.city}, {c.country}
              </span>
              {c.founded_year && (
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-wine-600" /> {t("founded_in")} {c.founded_year}
                </span>
              )}
              {c.employees_count && (
                <span className="inline-flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5 text-wine-600" /> {c.employees_count} {t("employees")}
                </span>
              )}
              {(c.special_tags || []).length > 0 && (
                <span className="mx-1 h-3 w-px bg-neutral-200" aria-hidden="true" />
              )}
              {(c.special_tags || []).map((tag) => (
                <span key={tag} className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-bold text-amber-700">
                  <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {tag}
                </span>
              ))}
              {(c.markets || []).map((tag) => (
                <span key={tag} className="rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs font-medium text-neutral-700">
                  {tag}
                </span>
              ))}
              {c.score !== null && c.score !== undefined && c.score > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-2.5 py-0.5 text-xs font-bold text-white shadow-sm" data-testid="member-score-badge" title={`Score communauté · Rang ${c.score_rank}/${c.score_total_companies}`}>
                  <Trophy className="h-3 w-3" /> {c.score} pts{c.score_percentile >= 75 ? ` · Top ${100 - c.score_percentile}%` : ""}
                </span>
              )}
              {c.rating_avg && (
                <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-bold text-amber-700" data-testid="member-rating-badge">
                  <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {c.rating_avg}/5 ({c.rating_count})
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-8 grid max-w-6xl gap-8 px-6 pb-16 md:grid-cols-3">
        {/* Main column */}
        <div className="md:col-span-2 space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-navy">{t("about")}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.description}</p>
            {c.long_description && (
              <div className="mt-4 whitespace-pre-line leading-relaxed text-neutral-700">{c.long_description}</div>
            )}
          </section>

          {(c.secondary_activities?.length > 0 || c.technologies?.length > 0) && (
            <section>
              <h2 className="text-2xl font-bold text-navy">{t("expertise")}</h2>
              {c.secondary_activities?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs font-bold uppercase tracking-wider text-neutral-500">{t("activities")}</div>
                  <div className="mt-2 flex flex-wrap gap-2">{c.secondary_activities.map((tag) => <span key={tag} className="rounded-full bg-wine-50 px-3 py-1 text-xs font-semibold text-wine-700">{tag}</span>)}</div>
                </div>
              )}
              {c.technologies?.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-neutral-500">{t("technologies")}</div>
                  <div className="mt-2 flex flex-wrap gap-2">{c.technologies.map((tag) => <span key={tag} className="rounded-full border border-wine-200 bg-white px-3 py-1 text-xs font-semibold text-wine-700">{tag}</span>)}</div>
                </div>
              )}
            </section>
          )}

          <section data-testid="member-public-wt-tags">
            <TagCategoryDisplay values={c} testId="member-public-tags" />
          </section>

          {products.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold text-navy"><Package className="inline h-5 w-5 text-wine-500" /> {t("products_services")}</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {products.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0)).map((p) => (
                  <div key={p.id} className="overflow-hidden rounded-2xl border border-neutral-100 bg-white" data-testid={`profile-product-${p.id}`}>
                    {p.image && <img src={resolveUrl(p.image)} alt={p.name} className="aspect-[16/9] w-full object-cover" />}
                    <div className="p-5">
                      <div className="flex items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${p.kind === "service" ? "bg-blue-50 text-blue-700" : "bg-wine-50 text-wine-700"}`}>{p.kind}</span>
                        {p.featured && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />}
                      </div>
                      <h3 className="mt-2 text-lg font-bold text-navy">{p.name}</h3>
                      <p className="mt-1 text-xs text-neutral-500">{p.category}</p>
                      <p className="mt-2 line-clamp-3 text-sm text-neutral-600">{p.short_description}</p>
                      <div className="mt-3 flex flex-wrap gap-3 text-[11px]">
                        {p.video_url && <a href={p.video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-wine-700"><Play className="h-3 w-3" /> {t("comp_memberprofile_92")}</a>}
                        {p.pdf_url && <a href={p.pdf_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-wine-700"><FileText className="h-3 w-3" /> PDF</a>}
                        {p.product_url && <a href={p.product_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-wine-700"><ExternalLink className="h-3 w-3" /> Site</a>}
                        {p.demo_url && <a href={p.demo_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-wine-700"><ExternalLink className="h-3 w-3" /> {t("comp_memberprofile_93")}</a>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {news.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold text-navy">{t("company_news")}</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {news.map((n) => (
                  <Link key={n.id} to={`/news/${n.slug}`} className="block rounded-xl border border-neutral-100 bg-white p-4 hover:bg-wine-50/40" data-testid={`member-related-news-${n.id}`}>
                    <span className="rounded-full bg-wine-50 px-2 py-0.5 text-[10px] font-bold uppercase text-wine-700">{n.category}</span>
                    <h4 className="mt-2 text-base font-bold text-navy">{n.title}</h4>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {(c.contacts || []).length > 0 && (
            <section data-testid="member-contacts">
              <h2 className="text-2xl font-bold text-navy"><Users className="inline h-5 w-5 text-wine-500" /> Contacts</h2>
              <p className="mt-1 text-sm text-neutral-600">{t("comp_memberprofile_94")}</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {c.contacts.map((p) => {
                  const initials = `${p.first_name?.[0] || ""}${p.last_name?.[0] || ""}`.toUpperCase() || "?";
                  return (
                    <div key={p.id} className="flex gap-3 rounded-2xl border border-neutral-100 bg-white p-4" data-testid={`member-contact-${p.id}`}>
                      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-wine-100 text-sm font-bold text-wine-700">{initials}</div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="truncate text-sm font-bold text-navy">{p.first_name} {p.last_name}</div>
                          {p.is_primary_contact && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800"><Crown className="h-2.5 w-2.5" />Principal</span>}
                        </div>
                        {p.job_title && <div className="text-xs text-neutral-500">{p.job_title}</div>}
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {p.email && <a href={`mailto:${p.email}`} className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-[10px] font-semibold text-neutral-700 hover:border-wine-300 hover:text-wine-700" data-testid={`contact-email-${p.id}`}><Mail className="h-3 w-3" />Email</a>}
                          {p.phone && <a href={`tel:${p.phone}`} className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-[10px] font-semibold text-neutral-700 hover:border-wine-300 hover:text-wine-700" data-testid={`contact-phone-${p.id}`}><Phone className="h-3 w-3" />{t("comp_memberprofile_95")}</a>}
                          {p.whatsapp && <a href={`https://wa.me/${p.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-100" data-testid={`contact-whatsapp-${p.id}`}><MessageCircle className="h-3 w-3" />WhatsApp</a>}
                          {p.linkedin && <a href={p.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-[#0A66C2]/30 bg-[#0A66C2]/5 px-2.5 py-1 text-[10px] font-semibold text-[#0A66C2] hover:bg-[#0A66C2]/10" data-testid={`contact-linkedin-${p.id}`}><Linkedin className="h-3 w-3" />LinkedIn</a>}
                          {p.calendar_link && <a href={p.calendar_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-100" data-testid={`contact-calendar-${p.id}`}><CalendarClock className="h-3 w-3" />{t("comp_memberprofile_96")}</a>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-3 rounded-2xl border border-neutral-100 bg-wine-50/20 p-5 h-fit">
          <div className="flex items-center gap-2 text-sm font-bold text-wine-700">
            <Wine className="h-4 w-4" /> {t("active_member")}
          </div>
          {c.tags?.length > 0 && (
            <div className="text-xs">
              <div className="font-bold uppercase tracking-wider text-neutral-500">{t("tags")}</div>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {c.tags.map((tag) => <span key={tag} className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-wine-700">#{tag}</span>)}
              </div>
            </div>
          )}
          {c.score > 0 && (
            <div className="text-xs">
              <div className="font-bold uppercase tracking-wider text-neutral-500">{t("community_score")}</div>
              <div className="mt-0.5 text-2xl font-bold text-wine-600">{c.score} {t("points")}</div>
            </div>
          )}
        </aside>
      </div>
    </PublicLayout>
  );
}
